import { isValidHexColor } from '@openx/utils/lib/isValidHexColor.ts';
import { useCallback } from 'react';
import type { PieLabelRenderProps } from 'recharts';

type UseLabelWithPositionProps = {
  data: {
    labelColor?: string;
  }[];
  labelColorDefault: string;
  totalValueSum: number;
};

export const useLabelWithPosition = ({ data, labelColorDefault, totalValueSum }: UseLabelWithPositionProps) => {
  const labelWithPosition = useCallback(
    ({ value, midAngle, innerRadius, outerRadius, index, cx, cy }: PieLabelRenderProps) => {
      if (totalValueSum === 0) {
        return null;
      }

      const percentage = (value / totalValueSum) * 100;
      if (percentage < 10) {
        return null;
      }

      if (midAngle === undefined) {
        return null;
      }

      const RADIAN = Math.PI / 180;
      const radius = innerRadius + (outerRadius - innerRadius) / 2;
      const x = cx + radius * Math.cos(-midAngle * RADIAN);
      const y = cy + radius * Math.sin(-midAngle * RADIAN);

      return (
        <text
          x={x}
          y={y}
          textAnchor="middle"
          dominantBaseline="central"
          fill={isValidHexColor(data[index].labelColor) ? data[index].labelColor : labelColorDefault}
          style={{ fontSize: '13px', fontWeight: 400 }}
          data-test="pie-chart-label"
        >
          {percentage.toFixed(1)}%
        </text>
      );
    },
    [data, labelColorDefault, totalValueSum]
  );

  return { labelWithPosition };
};
